/**
 * Shared policy + string helpers for RPA-scraped benefit HTML — the VSP
 * "sections" webhook schema delivers each portal section's benefits table as
 * raw HTML (see `VspSectionsBenefits` in eligibilityTypes.ts).
 *
 * Sanitization itself is delegated to community-maintained engines, one per
 * runtime (no single library covers both cleanly — DOMPurify needs a DOM,
 * sanitize-html is too heavy for the browser bundle):
 *   - ingest (Cloud Functions): sanitize-html — functions/src/eligibilityCheck/benefitHtml.ts
 *   - render (browser):         DOMPurify     — src/utils/benefitHtml.ts
 *
 * This module owns the SINGLE allowlist policy both engine configs consume,
 * so the two sides cannot drift apart. It must stay dependency-free: it
 * compiles into both package scopes (functions tsc and the Vite bundle).
 */

/** Tags that may survive sanitization — table/list/text structure only. */
export const BENEFIT_HTML_ALLOWED_TAGS: string[] = [
  "table", "colgroup", "col", "thead", "tbody", "tfoot", "tr", "th", "td",
  "ul", "ol", "li", "div", "span", "p", "b", "strong", "i", "em", "u",
  "br", "hr", "sup", "sub",
];

/**
 * Attributes that may survive, on any allowed tag. No URL-carrying or
 * scriptable attribute (src, href, style, on*, id, name, ...) is ever
 * allowed — that is what strips the portal's images and links.
 */
export const BENEFIT_HTML_ALLOWED_ATTRS: string[] = ["colspan", "rowspan", "class"];

/**
 * Elements whose content is code or metadata, never benefit text — the ingest
 * engine drops them together with everything inside. Passed to sanitize-html
 * as `nonTextTags`, which REPLACES the library default list, so this must be
 * a superset of that default (script, style, textarea, option, xmp).
 */
export const BENEFIT_HTML_DROP_CONTENT_TAGS: string[] = [
  "script", "style", "textarea", "option", "xmp", "noscript",
  "iframe", "object", "embed", "svg", "math", "head", "title",
  "template", "form", "select", "button",
];

/**
 * Collapse whitespace runs to single spaces — sheds the portal's heavy
 * indentation before storage (roughly halves the bytes). Rendering is
 * unaffected: HTML collapses whitespace anyway. Applied after sanitization
 * at ingest only. (JS `\s` also matches U+00A0, so a decoded `&nbsp;`
 * becomes a plain space — the portal only uses them decoratively.)
 */
export function collapseBenefitHtmlWhitespace(html: string): string {
  return html.replace(/\s+/g, " ").trim();
}

/**
 * Whether sanitized benefit HTML carries any visible text (a section whose
 * table is all markup, whitespace, and `&nbsp;` is not worth storing or
 * listing). Call this on SANITIZED html only — the sanitizers guarantee no
 * `>` inside attribute values, which the tag-stripping regex here relies on.
 * The literal `&nbsp;` branch covers docs stored before entity decoding
 * (the hand-rolled sanitizer era) — engines that decode entities emit U+00A0,
 * which `\s` already matches.
 */
export function benefitHtmlHasText(html: string): boolean {
  return (
    html
      .replace(/<[^>]*>/g, " ")
      .replace(/&(?:nbsp|#160|#xa0);/gi, " ")
      .replace(/\s+/g, "")
      .length > 0
  );
}
